import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { todayLocal, STATUS_TONES_BY_STATUS } from '@/lib/experienceStatus';

export { todayLocal };

export const STATUS_LABELS = {
  requested: 'Requested', pending: 'Pending', pending_coordinator_action: 'With Coordinator',
  pending_parent_confirmation: 'Awaiting Confirmation', confirmed: 'Confirmed', scheduled: 'Scheduled',
  awaiting_feedback: 'Awaiting Feedback', completed: 'Completed', cancelled: 'Cancelled', rejected: 'Declined',
  not_conducted: 'Did Not Take Place',
};

export const STATUS_COLORS = STATUS_TONES_BY_STATUS;

/** A mentor experience shaped like a booking, so the shared lifecycle rules apply. */
export const experienceBooking = (e) => ({
  status: e.status,
  assigned_date: e.date,
  assigned_time_slot: e.time,
  assigned_end_time: e.endTime,
  assigned_duration_minutes: e.durationMinutes,
  admin_closed_at: e.adminClosedAt,
});

export const PENDING_STATUSES = ['requested', 'pending', 'pending_coordinator_action', 'pending_parent_confirmation'];
export const UPCOMING_STATUSES = ['confirmed', 'scheduled'];
export const COMPLETED_STATUSES = ['completed', 'awaiting_feedback'];
export const CANCELLED_STATUSES = ['cancelled', 'rejected', 'not_conducted'];

export function fmtTime(t) {
  if (!t) return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10); const mm = m[2]; const ap = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${mm} ${ap}`;
}

export function isOfflineMode(mode) {
  const m = String(mode || '').toLowerCase();
  return m.includes('workplace') || m.includes('offline') || m.includes('person');
}

/** A session is created as soon as a mentor is assigned, so derive its real state
 *  from the bookings it contains. */
function effectiveSessionStatus(s) {
  const statuses = (s.students || []).map(st => st.status).filter(Boolean);
  if (s.status === 'scheduled' && statuses.length) {
    if (statuses.every(x => x === 'pending_parent_confirmation')) return 'pending_parent_confirmation';
    if (statuses.every(x => CANCELLED_STATUSES.includes(x))) return 'cancelled';
    if (statuses.every(x => COMPLETED_STATUSES.includes(x))) return 'completed';
  }
  return s.status;
}

export const TIMELINE_STAGES = [
  { id: 'requested', label: 'Requested' },
  { id: 'matched', label: 'Mentor matched' },
  { id: 'confirmed', label: 'Parent confirmed' },
  { id: 'completed', label: 'Session completed' },
  { id: 'feedback', label: 'Feedback submitted' },
  { id: 'certificates', label: 'Certificates generated' },
];

const latest = (dates) => dates.filter(Boolean).sort().slice(-1)[0] || null;

/** How far a session has got, from what is actually recorded against it. */
function buildTimeline({ status, students, createdAt, matchedAt, completedAt, certificatesAt, hasMentor }) {
  const sawConfirmation = students.some(s => ![...PENDING_STATUSES, 'requested'].includes(s.status))
    || [...UPCOMING_STATUSES, ...COMPLETED_STATUSES].includes(status);
  const feedbackAt = latest(students.map(s => s.feedback_submitted_at));
  const reached = {
    requested: { reached: true, date: createdAt },
    matched: { reached: hasMentor, date: matchedAt },
    confirmed: { reached: hasMentor && sawConfirmation, date: null },
    completed: { reached: !!completedAt || COMPLETED_STATUSES.includes(status), date: completedAt },
    feedback: { reached: !!feedbackAt, date: feedbackAt },
    certificates: { reached: !!certificatesAt || students.some(s => s.certificate_url), date: certificatesAt },
  };
  const stages = TIMELINE_STAGES.map((s, i) => ({ ...s, index: i, ...reached[s.id] }));
  const lastReached = [...stages].reverse().find(s => s.reached) || stages[0];
  return { stages, stage: lastReached };
}

function ratingOf(students) {
  const rated = students.filter(s => s.feedback_rating);
  return {
    average: rated.length ? rated.reduce((sum, s) => sum + s.feedback_rating, 0) / rated.length : null,
    count: rated.length,
    total: students.length,
  };
}

/** Build a de-duplicated experience list: group sessions + individual bookings not inside a session. */
export function buildExperiences(sessions, bookings) {
  const groupItems = (sessions || []).map(s => {
    const students = s.students || [];
    const status = effectiveSessionStatus(s);
    return {
    id: s.id,
    source: 'session',
    date: s.assigned_date,
    time: s.assigned_time_slot,
    endTime: s.assigned_end_time,
    durationMinutes: s.assigned_duration_minutes,
    career: s.career_name,
    status,
    mode: isOfflineMode(s.assigned_mode) ? 'Offline' : 'Online',
    offline: isOfflineMode(s.assigned_mode),
    studentCount: s.student_count || (s.students || []).length,
    venue: s.assigned_venue?.workplace_name || '',
    students: s.students || [],
    studentFeedback: (s.students || []).filter(st => st.feedback_rating).map(st => ({
      child_name: st.child_name,
      feedback_rating: st.feedback_rating,
      feedback_comment: st.feedback_comment,
      feedback_submitted_at: st.feedback_submitted_at,
    })),
    rating: ratingOf(students),
    timeline: buildTimeline({
      status, students, createdAt: s.created_at, matchedAt: s.created_at,
      completedAt: s.completed_at, certificatesAt: s.certificates_generated_at,
      hasMentor: true,
    }),
    link: `/group-sessions/${s.id}`,
  };
  });

  const individualItems = (bookings || []).filter(b => !b.session_id).map(b => {
    const mode = b.assigned_mode || b.requested_mode || b.mode || 'online';
    const students = [{
      booking_id: b.id, child_name: b.child_name, child_grade: b.child_grade,
      parent_name: b.parent_name, status: b.status, attended: b.attended,
      certificate_url: b.certificate_url, feedback_rating: b.feedback_rating,
      feedback_comment: b.feedback_comment, feedback_submitted_at: b.feedback_submitted_at,
    }];
    return {
      id: b.id,
      source: 'booking',
      date: b.assigned_date,
      time: b.assigned_time_slot || b.assigned_time,
      endTime: b.assigned_end_time,
      durationMinutes: b.assigned_duration_minutes,
      adminClosedAt: b.admin_closed_at,
      career: b.career_name,
      status: b.status,
      mode: isOfflineMode(mode) ? 'Offline' : 'Online',
      offline: isOfflineMode(mode),
      studentCount: 1,
      childName: b.child_name,
      venue: b.assigned_venue?.workplace_name || '',
      feedback_rating: b.feedback_rating,
      feedback_comment: b.feedback_comment,
      feedback_helpful: b.feedback_helpful,
      feedback_submitted_at: b.feedback_submitted_at,
      raw: b,
      students,
      rating: ratingOf(students),
      timeline: buildTimeline({
        status: b.status, students, createdAt: b.created_at, matchedAt: null,
        completedAt: b.completed_at, certificatesAt: null,
        hasMentor: !!b.mentor_profile_id || !!b.mentor_name,
      }),
      link: `/sessions/${b.id}`,
    };
  });

  return [...groupItems, ...individualItems].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}

/** All parent feedback entries for this mentor (includes bookings inside group sessions). */
export function buildFeedback(bookings) {
  return (bookings || [])
    .filter(b => b.feedback_rating)
    .sort((a, b) => String(b.feedback_submitted_at || '').localeCompare(String(a.feedback_submitted_at || '')));
}

export function ratingStats(feedbackList) {
  const total = feedbackList.length;
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  feedbackList.forEach(f => { distribution[f.feedback_rating] = (distribution[f.feedback_rating] || 0) + 1; });
  const average = total > 0 ? (feedbackList.reduce((s, f) => s + f.feedback_rating, 0) / total) : null;
  return { total, distribution, average };
}

/** Distinct students actually mentored: only completed experiences, de-duplicated. */
export function countStudents(sessions, bookings) {
  const ids = new Set();
  (bookings || []).forEach(b => {
    if (COMPLETED_STATUSES.includes(b.status) && (b.child_id || b.child_name)) ids.add(b.child_id || b.child_name);
  });
  (sessions || []).forEach(s => (s.students || []).forEach(st => {
    if (COMPLETED_STATUSES.includes(st.status) && (st.child_id || st.child_name)) ids.add(st.child_id || st.child_name);
  }));
  return ids.size;
}

export function useMentorData() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [declarationInfo, setDeclarationInfo] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [profRes, sessRes, bookRes, eligRes, declRes] = await Promise.all([
      api.get('/mentor-profile').catch(() => ({ data: { exists: false } })),
      api.get('/mentor/sessions').catch(() => ({ data: [] })),
      api.get('/mentor/bookings').catch(() => ({ data: [] })),
      api.get('/mentor-profile/eligibility').catch(() => ({ data: null })),
      api.get('/mentor-profile/declaration-info').catch(() => ({ data: null })),
    ]);
    setProfile(profRes.data?.exists ? profRes.data : null);
    setSessions(sessRes.data || []);
    setBookings(bookRes.data || []);
    setEligibility(eligRes.data);
    setDeclarationInfo(declRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { loading, profile, sessions, bookings, eligibility, declarationInfo, refresh };
}
